import { useEffect, useRef, useState } from "react";

/**
 * Ask until the answer lands, then give up HONESTLY.
 *
 * The clock both PayzMoon landings run. The redirect and the webhook are a race and the
 * webhook usually loses: the browser comes straight back from a checkout on somebody
 * else's domain while the grant is still a server-to-server hop away. So each landing
 * asks the server what actually happened, rather than believing a query string that came
 * back through the buyer's own browser.
 *
 * TIMING OUT IS NOT FAILING, which is the whole reason this is a shared clock and not two.
 * A webhook can be retried minutes later and the entitlement simply appears; telling
 * somebody their payment failed when it did not is far worse than telling them to check
 * back. `gaveUp` means "say it is on its way", never "it went wrong".
 *
 * What fulfillment IS stays with the caller — Gold reads an entitlement off the session,
 * the wall reads the board — and so does every word of copy. Only the asking is here.
 */
export function useUntilFulfilled({
  enabled,
  everyMs,
  giveUpMs,
  check,
}: {
  enabled: boolean;
  everyMs: number;
  giveUpMs: number;
  /** Ask once. Return true when it has landed; `live()` is false once this screen has
   *  gone, for callers that would otherwise write state into the void. */
  check: (live: () => boolean) => Promise<boolean | void>;
}): { gaveUp: boolean } {
  const [gaveUp, setGaveUp] = useState(false);
  // The clock starts at MOUNT and is never reset, so a re-render cannot quietly award
  // itself another forty-five seconds.
  const startedAt = useRef(Date.now());
  // The predicate by reference: it closes over fresh state every render, and naming it in
  // the deps would tear the timer down and rebuild it just as often.
  const checkRef = useRef(check);
  checkRef.current = check;

  useEffect(() => {
    if (!enabled) return;
    let stop = false;
    let timer = 0;
    const tick = async () => {
      if (stop) return;
      if (Date.now() - startedAt.current > giveUpMs) {
        setGaveUp(true);
        return;
      }
      try {
        if (await checkRef.current(() => !stop)) return;
      } catch {
        /* a blip is not an answer — keep asking until the clock says otherwise */
      }
      if (!stop) timer = window.setTimeout(() => void tick(), everyMs);
    };
    timer = window.setTimeout(() => void tick(), everyMs);
    return () => {
      stop = true;
      window.clearTimeout(timer);
    };
  }, [enabled, everyMs, giveUpMs]);

  return { gaveUp };
}
